import { useMemo, useState } from 'react'
import {
  BadgeStatus,
  Button,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { AkunKas, Pembayaran, Santri, Tagihan } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

const STATUS_PEMBAYARAN = ['DRAFT', 'VERIFIED', 'APPROVED', 'POSTED', 'RECONCILED', 'CANCELLED']
const METODE = [
  'CASH',
  'BANK_TRANSFER',
  'VIRTUAL_ACCOUNT',
  'QRIS',
  'EWALLET',
  'RETAIL_OUTLET',
  'CHEQUE',
  'OTHER',
]

export default function PembayaranPage() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })
  const [statusFilter, setStatusFilter] = useState('')

  const daftar = usePaged<Pembayaran>('/payments', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
      status: statusFilter || undefined,
    },
  })

  const aksi = useAksi()
  const [modalBuat, setModalBuat] = useState(false)
  const [konfirmasi, setKonfirmasi] = useState<{ jenis: 'verify' | 'post' | 'reverse'; data: Pembayaran } | null>(null)

  const kolom = useMemo<KolomTabel<Pembayaran>[]>(
    () => [
      {
        kunci: 'receiptNumber',
        judul: 'Nomor Kuitansi',
        render: (b) => <span className="font-medium text-netral-800">{b.receiptNumber}</span>,
      },
      {
        kunci: 'payerName',
        judul: 'Pembayar',
        nilaiSort: (b) => b.payerName ?? b.student?.person?.fullName ?? '',
        render: (b) => (
          <div>
            <p className="text-netral-800">{b.payerName ?? b.student?.person?.fullName ?? '—'}</p>
            <p className="text-xs text-netral-500">{b.student?.studentNumber ?? ''}</p>
          </div>
        ),
      },
      { kunci: 'paymentDate', judul: 'Tanggal', render: (b) => formatTanggal(b.paymentDate) },
      { kunci: 'paymentMethod', judul: 'Metode', render: (b) => b.paymentMethod },
      { kunci: 'cashAccount', judul: 'Kas/Bank', render: (b) => b.cashAccount?.name ?? '—' },
      { kunci: 'amount', judul: 'Jumlah', rata: 'kanan', render: (b) => formatRupiah(b.amount) },
      { kunci: 'allocatedAmount', judul: 'Teralokasi', rata: 'kanan', render: (b) => formatRupiah(b.allocatedAmount) },
      {
        kunci: 'unallocatedAmount',
        judul: 'Belum Teralokasi',
        rata: 'kanan',
        render: (b) => (
          <span className={b.unallocatedAmount > 0 ? 'font-medium text-emas-700' : ''}>
            {formatRupiah(b.unallocatedAmount)}
          </span>
        ),
      },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            {punyaIzin('payment:verify') && b.status === 'DRAFT' ? (
              <Button ukuran="kecil" onClick={() => setKonfirmasi({ jenis: 'verify', data: b })}>
                Verifikasi
              </Button>
            ) : null}
            {punyaIzin('payment:post') && (b.status === 'VERIFIED' || b.status === 'APPROVED') ? (
              <Button ukuran="kecil" varian="sekunder" onClick={() => setKonfirmasi({ jenis: 'post', data: b })}>
                Posting
              </Button>
            ) : null}
            {punyaIzin('payment:reverse') && b.status === 'POSTED' ? (
              <Button ukuran="kecil" varian="bahaya" onClick={() => setKonfirmasi({ jenis: 'reverse', data: b })}>
                Batalkan
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [punyaIzin],
  )

  const jalankanKonfirmasi = async (alasan: string) => {
    if (!konfirmasi) return
    const { jenis, data } = konfirmasi
    const rute =
      jenis === 'verify' ? 'verify' : jenis === 'post' ? 'post' : 'reverse'
    const badan = jenis === 'reverse' ? { reason: alasan } : {}
    const hasil = await aksi.jalankan(
      () => api.post(`/payments/${data.id}/${rute}`, badan, { idempotencyKey: buatIdempotencyKey() }),
      { sukses: `Pembayaran ${data.receiptNumber} berhasil diproses.` },
    )
    if (hasil !== null) {
      setKonfirmasi(null)
      daftar.muat()
    }
  }

  return (
    <div>
      <PageHeader
        judul="Pembayaran"
        deskripsi="Pencatatan penerimaan pembayaran dan alokasinya ke tagihan santri/siswa."
        ekspor={{
          path: '/reports/receipts',
          namaDasar: 'pembayaran',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined, unitId: filter.unitId || undefined },
        }}
        aksi={
          punyaIzin('payment:create') ? <Button onClick={() => setModalBuat(true)}>+ Catat Pembayaran</Button> : null
        }
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ kategori: false, program: false, fundId: false }}
        ekstra={
          <Select
            label="Status"
            placeholder="Semua status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            opsi={STATUS_PEMBAYARAN.map((s) => ({ value: s, label: s }))}
            className="min-w-40"
          />
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        onUrut={daftar.setUrutan}
        urutSaatIni={daftar.urutan}
        pesanKosong="Belum ada pembayaran pada filter ini."
      />

      <FormCatatPembayaran
        terbuka={modalBuat}
        onTutup={() => setModalBuat(false)}
        unit={opsiUnit}
        setelah={daftar.muat}
      />

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={
          konfirmasi?.jenis === 'verify'
            ? 'Verifikasi Pembayaran'
            : konfirmasi?.jenis === 'post'
              ? 'Posting Pembayaran'
              : 'Batalkan Pembayaran'
        }
        pesan={
          konfirmasi?.jenis === 'reverse'
            ? `Pembayaran ${konfirmasi?.data.receiptNumber} sebesar ${formatRupiah(konfirmasi?.data.amount ?? 0)} akan dibatalkan dan jurnalnya dibalik.`
            : `Lanjutkan aksi untuk pembayaran ${konfirmasi?.data.receiptNumber} sebesar ${formatRupiah(konfirmasi?.data.amount ?? 0)}?`
        }
        labelKonfirmasi={konfirmasi?.jenis === 'reverse' ? 'Batalkan Pembayaran' : 'Lanjutkan'}
        berbahaya={konfirmasi?.jenis === 'reverse'}
        labelAlasan={konfirmasi?.jenis === 'reverse' ? 'Alasan pembatalan' : undefined}
        minimalAlasan={10}
        memuat={aksi.memuat}
        onKonfirmasi={(alasan) => void jalankanKonfirmasi(alasan)}
      />
    </div>
  )
}

function FormCatatPembayaran({
  terbuka,
  onTutup,
  unit,
  setelah,
}: {
  terbuka: boolean
  onTutup: () => void
  unit: Array<{ value: string; label: string }>
  setelah: () => void
}) {
  const [unitId, setUnitId] = useState('')
  const [studentId, setStudentId] = useState('')
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER')
  const [cashAccountId, setCashAccountId] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [payerName, setPayerName] = useState('')
  const [notes, setNotes] = useState('')
  const [alokasi, setAlokasi] = useState<Record<string, string>>({})
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const santri = useApi<unknown>('/students', { perPage: 200, unitId: unitId || undefined })
  const daftarSantri: Santri[] = Array.isArray(santri.data)
    ? (santri.data as Santri[])
    : ((santri.data as { items?: Santri[] } | null)?.items ?? [])

  const kas = useApi<unknown>('/cash-accounts', { perPage: 200, unitId: unitId || undefined })
  const daftarKas: AkunKas[] = Array.isArray(kas.data)
    ? (kas.data as AkunKas[])
    : ((kas.data as { items?: AkunKas[] } | null)?.items ?? [])

  const tagihan = useApi<unknown>(studentId ? '/invoices' : null, {
    studentId,
    perPage: 50,
    status: 'UNPAID',
  })
  const daftarTagihan: Tagihan[] = Array.isArray(tagihan.data)
    ? (tagihan.data as Tagihan[])
    : ((tagihan.data as { items?: Tagihan[] } | null)?.items ?? [])

  const totalAlokasi = Object.values(alokasi).reduce((a, v) => a + (Number(v) || 0), 0)

  const simpan = async () => {
    setGalat(null)
    if (!unitId) return setGalat('Unit wajib dipilih.')
    const nilai = Number(amount.replace(/[^0-9.-]/g, ''))
    if (!nilai || nilai <= 0) return setGalat('Jumlah pembayaran harus lebih dari nol.')
    if (totalAlokasi > nilai) return setGalat('Total alokasi melebihi jumlah pembayaran.')

    setMemuat(true)
    try {
      const hasil = await api.post<{ id: string; receiptNumber?: string }>(
        '/payments',
        {
          unitId,
          studentId: studentId || undefined,
          payerName: payerName.trim() || undefined,
          paymentDate,
          amount: nilai,
          paymentMethod,
          cashAccountId: cashAccountId || undefined,
          referenceNumber: referenceNumber.trim() || undefined,
          notes: notes.trim() || undefined,
        },
        { idempotencyKey: buatIdempotencyKey() },
      )

      const pembayaranId = hasil?.id
      const alokasiAktif = Object.entries(alokasi).filter(([, v]) => Number(v) > 0)
      if (pembayaranId && alokasiAktif.length > 0) {
        for (const [invoiceId, nilaiAlokasi] of alokasiAktif) {
          await api.post(`/payments/${pembayaranId}/allocations`, {
            invoiceId,
            allocatedAmount: Number(nilaiAlokasi),
          })
        }
      }
      setAlokasi({})
      setAmount('')
      setelah()
      onTutup()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Catat Pembayaran"
      deskripsi="Pembayaran dicatat berstatus draf, lalu diverifikasi dan diposting."
      lebar="max-w-3xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Simpan Pembayaran
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}

        <KolomForm kolom={2}>
          <Select
            label="Unit"
            placeholder="Pilih unit"
            opsi={unit}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            required
          />
          <Select
            label="Santri/Siswa"
            placeholder="Pilih santri (opsional)"
            opsi={daftarSantri.map((s) => ({
              value: s.id,
              label: `${s.studentNumber} — ${s.person?.fullName ?? s.id}`,
            }))}
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
          />
          <Input label="Nama Pembayar" value={payerName} onChange={(e) => setPayerName(e.target.value)} />
          <Input
            type="date"
            label="Tanggal Pembayaran"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
          />
          <Input
            label="Jumlah (Rp)"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <Select
            label="Metode Pembayaran"
            opsi={METODE.map((m) => ({ value: m, label: m }))}
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          <Select
            label="Masuk ke Kas/Bank"
            placeholder="Pilih akun kas/bank"
            opsi={daftarKas.map((k) => ({ value: k.id, label: `${k.code} — ${k.name}` }))}
            value={cashAccountId}
            onChange={(e) => setCashAccountId(e.target.value)}
            bantuan={kas.galat ? `Akun kas/bank belum dapat dimuat: ${kas.galat}` : undefined}
          />
          <Input
            label="Nomor Referensi"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
          />
        </KolomForm>

        {studentId ? (
          <div className="rounded-xl border border-netral-200 p-3">
            <h3 className="text-sm font-semibold text-netral-800">Alokasi ke Tagihan</h3>
            <p className="mt-0.5 text-xs text-netral-500">
              Isi jumlah untuk tagihan yang dibayar. Total alokasi: {formatRupiah(totalAlokasi)}
            </p>
            {tagihan.memuat ? (
              <p className="mt-2 text-sm text-netral-500">Memuat tagihan…</p>
            ) : tagihan.galat ? (
              <p className="mt-2 text-sm text-red-600">{tagihan.galat}</p>
            ) : daftarTagihan.length === 0 ? (
              <p className="mt-2 text-sm text-netral-500">Tidak ada tagihan belum lunas untuk santri ini.</p>
            ) : (
              <div className="mt-2 space-y-2">
                {daftarTagihan.map((t) => (
                  <div
                    key={t.id}
                    className="grid grid-cols-1 items-center gap-2 rounded-lg bg-netral-50 p-2 sm:grid-cols-[1fr_160px]"
                  >
                    <div className="text-sm">
                      <p className="font-medium text-netral-800">{t.invoiceNumber}</p>
                      <p className="text-xs text-netral-500">
                        Jatuh tempo {formatTanggal(t.dueDate)} · Sisa {formatRupiah(t.outstandingAmount)}
                      </p>
                    </div>
                    <Input
                      placeholder="Jumlah alokasi"
                      inputMode="numeric"
                      value={alokasi[t.id] ?? ''}
                      onChange={(e) => setAlokasi((a) => ({ ...a, [t.id]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        <Textarea label="Catatan" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>
    </Modal>
  )
}
